// «Detalle por líneas» de una nota de crédito/débito de Farmacia sobre una factura de tarifas mezcladas. Solo presenta: la cuenta
// viene de `calcularNota` (lib/notaLineas.ts), copia atada al motor de impuestos del servidor.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { CANTIDAD_MAX, DESCRIPCION_MAX, centavosADolares, type FilaNota, type ResultadoNota } from '@/lib/notaLineas';

export function EditorLineasNotaFarmacia({
  filas,
  resultado,
  tipo,
  onChange,
}: {
  filas: FilaNota[];
  resultado: ResultadoNota;
  tipo: 'nc' | 'nd';
  onChange: (filas: FilaNota[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  function cambiar(i: number, parche: Partial<FilaNota>): void {
    onChange(filas.map((f, k) => (k === i ? { ...f, ...parche } : f)));
  }
  // En una nota de crédito la descripción y el precio son los facturados (el servidor exige que cada línea sea una de la factura).
  const fijo = tipo === 'nc';
  return (
    <fieldset className="m-0 w-full min-w-0 space-y-2 border-0 p-0" aria-label={t('farmaciaApp.comprobantes.detalle.titulo')}>
      <p className="text-xs text-hce-muted">{t('farmaciaApp.comprobantes.detalle.ayuda')}</p>
      {filas.map((f, i) => (
        <div key={`linea-${i}`} className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Input
              aria-label={t('farmaciaApp.comprobantes.detalle.descripcion', { n: i + 1 })}
              className="h-9"
              value={f.description}
              maxLength={DESCRIPCION_MAX}
              readOnly={fijo}
              onChange={(e) => cambiar(i, { description: e.target.value })}
            />
          </div>
          <label className="text-xs text-hce-muted">
            {t('farmaciaApp.comprobantes.detalle.cantidad')}
            <Input type="number" step="1" min="0" max={CANTIDAD_MAX} className="h-9 w-20" value={f.cantidad} onChange={(e) => cambiar(i, { cantidad: e.target.value })} />
          </label>
          <label className="text-xs text-hce-muted">
            {t('farmaciaApp.comprobantes.detalle.precio')}
            <Input type="number" step="0.01" min="0.01" className="h-9 w-28" value={f.precio} readOnly={fijo} onChange={(e) => cambiar(i, { precio: e.target.value })} />
          </label>
          <span className="pb-2 text-xs text-hce-muted">{t('farmaciaApp.comprobantes.detalle.iva', { tarifa: f.ivaRate })}</span>
        </div>
      ))}
      <dl className="grid max-w-xs grid-cols-[1fr_auto] gap-x-3 text-xs text-hce-ink">
        <dt>{t('farmaciaApp.comprobantes.detalle.subtotal')}</dt>
        <dd className="text-right tabular-nums">USD {centavosADolares(resultado.subtotalCents)}</dd>
        <dt>{t('farmaciaApp.comprobantes.detalle.ivaTotal')}</dt>
        <dd className="text-right tabular-nums">USD {centavosADolares(resultado.ivaCents)}</dd>
        <dt className="font-semibold">{t('farmaciaApp.comprobantes.detalle.total')}</dt>
        <dd className="text-right font-semibold tabular-nums">USD {centavosADolares(resultado.totalCents)}</dd>
      </dl>
      {resultado.error && (
        <p role="alert" className="text-xs text-red-700">
          {resultado.error}
        </p>
      )}
    </fieldset>
  );
}
